import { useEffect, useState } from 'react'

import type { FlashMessage } from './types'
import { deleteFlashMessages, emitter, readFlashMessages } from './utils.ts'

const useFlashMessages = () => {
  const [flashMessages, setFlashMessages] = useState<FlashMessage[]>([])

  // creates the flash messages, triggering a rerender
  useEffect(() => {
    const onFlashMessage = () => {
      const messages = readFlashMessages()
      setFlashMessages(messages)
    }

    // respond to flash_message events
    emitter.on('flash_message', onFlashMessage)

    // run on load
    onFlashMessage()

    return () => {
      // prevent memory leaks
      emitter.off('flash_message', onFlashMessage)
    }
  }, [])

  useEffect(() => {
    let timeout: NodeJS.Timeout

    /**
     * We clear the cookie after 1 second to make sure that the flash messages are
     * actually displayed to users when added. This works around the following use case:
     *
     * 1. perform some action like update a user's password
     * 2. set the flash message cookie
     * 3. emitter is triggered and the flash message is rendered and removed from the cookie
     * 4. the user is immediately redirected to a new page (without having time to see the flash message)
     *
     * With the below, after flashMessages has changed, the cookie will only be cleared if the user hasn't
     * been redirected to a new page within 1 second.
     */
    if (flashMessages) {
      timeout = setTimeout(() => {
        deleteFlashMessages()
      }, 1000)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [flashMessages])

  return flashMessages
}

export default useFlashMessages
